import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import {
  NavCategory,
  NavCategoryItem,
  NavDrawer,
  NavDrawerBody,
  NavItem,
  NavSubItem,
  NavSubItemGroup,
} from "@fluentui/react-components";
import "../CSS/SideBar.css";
import { makeStyles, tokens } from "@fluentui/react-components";
import { AiOutlineHome } from "react-icons/ai";
import { FiSettings } from "react-icons/fi";
import { TiGroupOutline } from "react-icons/ti";
import { RiUserSettingsLine, RiCustomerService2Line, RiRobot2Line } from "react-icons/ri";
import { CiUser } from "react-icons/ci";
import { ImFileMusic } from "react-icons/im";
import { AiOutlineSafetyCertificate } from "react-icons/ai";
import { MdOutlineAnalytics, MdOutlineVideoLibrary, MdSpeed, MdOutlineMail, MdOutlineDescription, MdOutlineSms } from "react-icons/md";
import { FaWhatsapp } from "react-icons/fa";
import { MdAccountTree } from "react-icons/md";
import { BiData } from "react-icons/bi";
import { PanelLeft24Regular, PanelRight24Regular } from "@fluentui/react-icons";
import QueueIcon from "../../Assets/Icons/Queues.webp";
import CallFlowIcon from "../../Assets/Icons/call-flows.png";
import ChatFlowIcon from "../../Assets/Icons/chat-flows.webp";
import { useSelector } from "react-redux";
import { useAbility } from "../../Contexts/AbilityContext";
import packageJson from "../../../package.json";

const useStyles = makeStyles({
  root: { overflow: "hidden", display: "flex", height: "100vh" },
  nav: { minWidth: "150px" },
  content: { flex: "1", padding: "16px", display: "grid", justifyContent: "flex-start", alignItems: "flex-start" },
});

const SideBar = ({ SCOPES_NAMES }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const ability = useAbility();
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Feature flag par tenant : l'agent IA n'est visible que pour les tenants dont
  // le doc porte features.iaAgent === true (objet tenant déjà chargé dans Redux).
  const partnerTenants = useSelector((s) => s.reducer.partnerTenants);
  const activeTenantId = useSelector((s) => s.reducer.ACTIVE_TENANT);
  const iaAgentEnabled = useMemo(() => {
    const at = Array.isArray(partnerTenants)
      ? partnerTenants.find((t) => t.tenantId === activeTenantId)
      : null;
    return !!at?.features?.iaAgent;
  }, [partnerTenants, activeTenantId]);

  const toggleSidebar = () => setIsCollapsed(!isCollapsed);

  // Vérifie si l'utilisateur a au moins une permission (read/create/update/delete) sur main dans au moins un scope
  const hasAnyPermissionOnMain = (module) => {
    if (!ability || !SCOPES_NAMES) {
      return false;
    }

    const actions = ["read", "create", "update", "delete"];
    const result = SCOPES_NAMES.some(scope => {
      const scopeResult = actions.some(action => {
        const canDo = ability.can(action, `${module}.main@${scope}`);
        if (canDo) {
        }
        return canDo;
      });
      return scopeResult;
    });

    return result;
  };

  return (
    <div className={`sidebar-wrapper ${isCollapsed ? "sidebar-collapsed" : ""}`}>
      <NavDrawer
        defaultSelectedValue="1"
        defaultSelectedCategoryValue="1"
        open
        type="inline"
        className={`${styles.nav} ${isCollapsed ? "nav-collapsed" : "nav-expanded"}`}
      >
        <NavDrawerBody>
          {/* Toggle Button */}
          <NavItem
            icon={isCollapsed ? <PanelRight24Regular /> : <PanelLeft24Regular />}
            value="toggle"
            onClick={toggleSidebar}
            className="sidebar-toggle-item"
          />

          {/* Always visible items */}
          <NavItem
            icon={<AiOutlineHome className="licence-icon-style" />}
            value="1"
            className={`nav-item ${location.pathname === "/" ? "active" : ""}`}
            onClick={() => navigate("/")}
            title={isCollapsed ? t("HOME") : ""}
          >
            {!isCollapsed && t("HOME")}
          </NavItem>

          {hasAnyPermissionOnMain("GlobalSettings") && (
            <NavItem
              icon={<FiSettings className="licence-icon-style" />}
              value="2"
              className={`nav-item ${location.pathname.startsWith("/globalSettings") ? "active" : ""}`}
              onClick={() => navigate("/globalSettings")}
              title={isCollapsed ? t("GLOBAL_SETTING") : ""}
            >
              {!isCollapsed && t("GLOBAL_SETTING")}
            </NavItem>
          )}

          {hasAnyPermissionOnMain("Entity") && (
            <NavItem
              icon={<RiUserSettingsLine className="licence-icon-style" />}
              value="3"
              className={`nav-item ${location.pathname.startsWith("/entities") ? "active" : ""}`}
              onClick={() => navigate("/entities")}
              title={isCollapsed ? t("ADMINISTRATIONS") : ""}
            >
              {!isCollapsed && t("ADMINISTRATIONS")}
            </NavItem>
          )}

          {hasAnyPermissionOnMain("Group") && (
            <NavItem
              icon={<TiGroupOutline className="licence-icon-style" />}
              value="4"
              className={`nav-item ${location.pathname.startsWith("/groups") ? "active" : ""}`}
              onClick={() => navigate("/groups")}
              title={isCollapsed ? t("GROUPS") : ""}
            >
              {!isCollapsed && t("GROUPS")}
            </NavItem>
          )}

          {hasAnyPermissionOnMain("Flow") && (
            <NavItem
              icon={<FaWhatsapp className="licence-icon-style" />}
              value="4.5"
              className={`nav-item ${location.pathname.startsWith("/whatsapp") ? "active" : ""}`}
              onClick={() => navigate("/whatsapp")}
              title={isCollapsed ? "WhatsApp Flows" : ""}
            >
              {!isCollapsed && "WhatsApp Flows"}
            </NavItem>
          )}

          {/* Conditional items */}
          {hasAnyPermissionOnMain("Queue") && (
            <NavItem
              icon={<img src={QueueIcon} alt="Queues" className="icon-style" />}
              value="5"
              className={`nav-item ${location.pathname.startsWith("/queues") ? "active" : ""}`}
              onClick={() => navigate("/queues")}
              title={isCollapsed ? t("QUEUES") : ""}
            >
              {!isCollapsed && t("QUEUES")}
            </NavItem>
          )}

          {hasAnyPermissionOnMain("Flow") && (
            <NavItem
              icon={<img src={CallFlowIcon} alt="Call Flows" className="icon-style" />}
              value="6"
              className={`nav-item ${location.pathname.startsWith("/callFlows") ? "active" : ""}`}
              onClick={() => navigate("/callFlows")}
              title={isCollapsed ? t("CALL_FLOWS") : ""}
            >
              {!isCollapsed && t("CALL_FLOWS")}
            </NavItem>
          )}

          {hasAnyPermissionOnMain("Flow") && iaAgentEnabled && (
            <NavItem
              icon={<RiRobot2Line className="licence-icon-style" />}
              value="6.5"
              className={`nav-item ${location.pathname.startsWith("/iaagent") ? "active" : ""}`}
              onClick={() => navigate("/iaagent")}
              title={isCollapsed ? t("AGENT_AI") : ""}
            >
              {!isCollapsed && t("AGENT_AI")}
            </NavItem>
          )}
          {/*
            MENU "Variables" MASQUÉ VOLONTAIREMENT.
            La fonctionnalité n'est pas encore implémentée côté backend : afficher cette
            entrée (lien vers /variables) ne fait que créer de la confusion pour les admins.
            À RÉACTIVER quand le back sera prêt : il suffit de décommenter le bloc ci-dessous.
          */}
          {false && hasAnyPermissionOnMain("CustomerDB") && (
            <NavItem
              icon={<BiData className="licence-icon-style" />}
              value="16"
              className={`nav-item ${location.pathname.startsWith("/variables") ? "active" : ""}`}
              onClick={() => navigate("/variables")}
              title={isCollapsed ? t("VARIABLES") : ""}
            >
              {!isCollapsed && t("VARIABLES")}
            </NavItem>
          )}

         {hasAnyPermissionOnMain("Workflows") && ( 
            <NavItem
              icon={<MdAccountTree className="licence-icon-style" />}
              value="17"
              className={`nav-item ${location.pathname.startsWith("/workflows") ? "active" : ""}`}
              onClick={() => navigate("/workflows")}
              title={isCollapsed ? t("WORKFLOWS") : ""}
            >
              {!isCollapsed && t("WORKFLOWS")}
            </NavItem>
         )}
          {hasAnyPermissionOnMain("Flow") && (
            <NavItem
              icon={<img src={ChatFlowIcon} alt="Chat Flows" className="icon-style" />}
              value="7"
              className={`nav-item ${location.pathname.startsWith("/chatFlows") ? "active" : ""}`}
              onClick={() => navigate("/chatFlows")}
              title={isCollapsed ? t("CHAT_FLOWS") : ""}
            >
              {!isCollapsed && t("CHAT_FLOWS")}
            </NavItem>
          )}

          {/* MAIL — catégorie à deux entrées, sur le patron du menu Agents : le flux décrit OÙ
              va un mail, le gabarit décrit CE QU'ON RÉPOND. Deux écrans, deux conteneurs Cosmos,
              deux moments du travail d'un administrateur. Repli en icônes seules quand la barre
              est repliée, comme les autres catégories. */}
          {hasAnyPermissionOnMain("Flow") &&
            (!isCollapsed ? (
              <NavCategory value="7.5">
                <NavCategoryItem
                  icon={<MdOutlineMail className="licence-icon-style" />}
                  value="7.51"
                >
                  {t("MAIL")}
                </NavCategoryItem>
                <NavSubItemGroup>
                  <NavSubItem
                    icon={<MdOutlineMail className="licence-icon-style" />}
                    value="7.52"
                    className={`nav-item ${location.pathname.startsWith("/mailFlows") ? "active" : ""}`}
                    onClick={() => navigate("/mailFlows")}
                  >
                    {t("MAIL_FLOWS")}
                  </NavSubItem>
                  <NavSubItem
                    icon={<MdOutlineDescription className="licence-icon-style" />}
                    value="7.53"
                    className={`nav-item ${location.pathname.startsWith("/mailTemplates") ? "active" : ""}`}
                    onClick={() => navigate("/mailTemplates")}
                  >
                    {t("MAIL_TEMPLATES")}
                  </NavSubItem>
                  {/* Quick SMS. Rangé sous « Mail » plutôt que dans sa propre catégorie tant
                      qu'il n'y a qu'une seule entrée : une catégorie à un élément coûte un clic
                      pour rien. Elle se détachera le jour où un « SMS Flows » existera — le SMS
                      entrant, qui n'est pas au programme. */}
                  <NavSubItem
                    icon={<MdOutlineSms className="licence-icon-style" />}
                    value="7.54"
                    className={`nav-item ${location.pathname.startsWith("/smsTemplates") ? "active" : ""}`}
                    onClick={() => navigate("/smsTemplates")}
                  >
                    {t("SMS_TEMPLATES")}
                  </NavSubItem>
                </NavSubItemGroup>
              </NavCategory>
            ) : (
              <>
                <NavItem
                  icon={<MdOutlineMail className="licence-icon-style" />}
                  value="7.52"
                  className={`nav-item ${location.pathname.startsWith("/mailFlows") ? "active" : ""}`}
                  onClick={() => navigate("/mailFlows")}
                  title={t("MAIL_FLOWS")}
                />
                <NavItem
                  icon={<MdOutlineDescription className="licence-icon-style" />}
                  value="7.53"
                  className={`nav-item ${location.pathname.startsWith("/mailTemplates") ? "active" : ""}`}
                  onClick={() => navigate("/mailTemplates")}
                  title={t("MAIL_TEMPLATES")}
                />
                <NavItem
                  icon={<MdOutlineSms className="licence-icon-style" />}
                  value="7.54"
                  className={`nav-item ${location.pathname.startsWith("/smsTemplates") ? "active" : ""}`}
                  onClick={() => navigate("/smsTemplates")}
                  title={t("SMS_TEMPLATES")}
                />
              </>
            ))}

          {hasAnyPermissionOnMain("Media") && (
            <NavItem
              icon={<ImFileMusic className="licence-icon-style" />}
              value="12"
              className={`nav-item ${location.pathname.startsWith("/medias") ? "active" : ""}`}
              onClick={() => navigate("/medias")}
              title={isCollapsed ? t("MEDIA") : ""}
            >
              {!isCollapsed && t("MEDIA")}
            </NavItem>
          )}

          {hasAnyPermissionOnMain("Licences") && (
            <NavItem
              icon={<AiOutlineSafetyCertificate className="licence-icon-style" />}
              value="13"
              className={`nav-item ${location.pathname === "/licences" ? "active" : ""}`}
              onClick={() => navigate("/licences")}
              title={isCollapsed ? t("LICENCES") : ""}
            >
              {!isCollapsed && t("LICENCES")}
            </NavItem>
          )}

          {hasAnyPermissionOnMain("Recordings") && (
            <NavItem
              icon={<MdOutlineVideoLibrary className="licence-icon-style" />}
              value="15"
              className={`nav-item ${location.pathname.startsWith("/recording") ? "active" : ""}`}
              onClick={() => navigate("/recording")}
              title={isCollapsed ? t("RECORDING") : ""}
            >
              {!isCollapsed && t("RECORDING")}
            </NavItem>
          )}



          {hasAnyPermissionOnMain("Agent") &&
            (!isCollapsed ? (
              <NavCategory value="8">
                <NavCategoryItem
                  icon={<RiCustomerService2Line className="licence-icon-style" />}
                  value="9"
                >
                  {t("AGENTS")}
                </NavCategoryItem>
                <NavSubItemGroup>
                  <NavSubItem
                    icon={<RiCustomerService2Line className="licence-icon-style" />}
                    value="10"
                    className={`nav-item ${(location.pathname === "/agents" || (location.pathname.startsWith("/agents/") && !location.pathname.startsWith("/agentsProfiles"))) ? "active" : ""}`}
                    onClick={() => navigate("/agents")}
                    title={isCollapsed ? t("AGENT_LIST") : ""}
                  >
                    {t("AGENT_LIST")}
                  </NavSubItem>
                  <NavSubItem
                    icon={<CiUser className="licence-icon-style" />}
                    value="11"
                    className={`nav-item ${location.pathname.startsWith("/agentsProfiles") ? "active" : ""}`}
                    onClick={() => navigate("/agentsProfiles")}
                    title={isCollapsed ? t("AGENTS_PROFILES") : ""}
                  >
                    {t("AGENTS_PROFILES")}
                  </NavSubItem>
                </NavSubItemGroup>
              </NavCategory>
            ) : (
              <>
                <NavItem
                  icon={<RiCustomerService2Line className="licence-icon-style" />}
                  value="10"
                  className={`nav-item ${(location.pathname === "/agents" || (location.pathname.startsWith("/agents/") && !location.pathname.startsWith("/agentsProfiles"))) ? "active" : ""}`}
                  onClick={() => navigate("/agents")}
                  title={t("AGENT_LIST")}
                />
                <NavItem
                  icon={<CiUser className="licence-icon-style" />}
                  value="11"
                  className={`nav-item ${location.pathname.startsWith("/agentsProfiles") ? "active" : ""}`}
                  onClick={() => navigate("/agentsProfiles")}
                  title={t("AGENTS_PROFILES")}
                />
              </>
            ))}

          {hasAnyPermissionOnMain("Analytics") &&
            (!isCollapsed ? (
              <NavCategory value="14">
                <NavCategoryItem
                  icon={<MdOutlineAnalytics className="licence-icon-style" />}
                  value="14.1"
                >
                  {t("ANALYTICS")}
                </NavCategoryItem>
                <NavSubItemGroup>
                  <NavSubItem
                    icon={<MdOutlineAnalytics className="licence-icon-style" />}
                    value="14.2"
                    className={`nav-item ${(location.pathname === "/analytics" || location.pathname.startsWith("/analytics/")) ? "active" : ""}`}
                    onClick={() => navigate("/analytics")}
                    title={isCollapsed ? t("ANALYTICS") : ""}
                  >
                    {t("ANALYTICS")}
                  </NavSubItem>
                  <NavSubItem
                    icon={<MdOutlineAnalytics className="licence-icon-style" />}
                    value="14.3"
                    className={`nav-item ${location.pathname.startsWith("/analyticsProfile") ? "active" : ""}`}
                    onClick={() => navigate("/analyticsProfile")}
                    title={isCollapsed ? t("ANALYTICS_PROFILE") : ""}
                  >
                    {t("ANALYTICS_PROFILE")}
                  </NavSubItem>
                </NavSubItemGroup>
              </NavCategory>
            ) : (
              <>
                <NavItem
                  icon={<MdOutlineAnalytics className="licence-icon-style" />}
                  value="14.2"
                  className={`nav-item ${(location.pathname === "/analytics" || location.pathname.startsWith("/analytics/")) ? "active" : ""}`}
                  onClick={() => navigate("/analytics")}
                  title={t("ANALYTICS")}
                />
                <NavItem
                  icon={<MdOutlineAnalytics className="licence-icon-style" />}
                  value="14.3"
                  className={`nav-item ${location.pathname.startsWith("/analyticsProfile") ? "active" : ""}`}
                  onClick={() => navigate("/analyticsProfile")}
                  title={t("ANALYTICS_PROFILE")}
                />
              </>
            ))}

            {hasAnyPermissionOnMain("realTimeDashboard") && (
            <NavItem
              icon={<MdSpeed className="licence-icon-style" />}
              value="16"
              className={`nav-item ${location.pathname.startsWith("/realtime-dashboard") ? "active" : ""}`}
              onClick={() => navigate("/realtime-dashboard")}
              title={isCollapsed ? t("REALTIME_DASHBOARD") : ""}
            >
              {!isCollapsed && t("REALTIME_DASHBOARD")}
            </NavItem>
          )}
          
          {/* Version Footer */}
          {!isCollapsed && (
            <div className="sidebar-version-footer">
              <span>V{packageJson.version}</span>
            </div>)}

        
        </NavDrawerBody>
      </NavDrawer>
    </div>
  );
};

export default SideBar;
